/* eslint-disable @typescript-eslint/no-explicit-any */
import { deepCopy } from '../../helpers';
import { nearestSixteenth } from '../../helpers/nearest-sixteenth';
import { SURROUND_MATERIAL_THICKNESS, type SurroundMaterialRoleThickness } from '../../blueprint-classes/surround-material/constants';

/**
 * Schema update from version 14 to version 15
 * - Recalculates corner island (5-panel) panel sizes so that:
 *   1. The front panel width is the fixed TARGET_FRONT_PANEL_WIDTH (36)
 *   2. Back panel widths are rounded up to the nearest 1/16"
 *   3. Side panel widths are recalculated from depth − front − back thickness
 */

const TARGET_FRONT_PANEL_WIDTH = 36;

const getThicknessForRole = (materialType: string, role: string): number => {
	const entry = SURROUND_MATERIAL_THICKNESS[materialType];
	if (entry === undefined) return 0;
	if (typeof entry === 'number') return entry;
	return (entry as SurroundMaterialRoleThickness)[role] ?? 0;
};

const findPanelById = (panels: Array<any>, id: string): any =>
	panels.find((p: any) => p.id === id) ?? null;

const fixCornerPanelSizes = (island: any, kitchen: any): void => {
	const { panels } = island;
	if (!panels?.children?.length || panels.children.length !== 5) return;

	const materialType = panels.surroundMaterial?.key
		?? panels.surroundMaterial?.type
		?? island.surround?.key
		?? island.surround?.type
		?? kitchen?.material?.surround?.key
		?? kitchen?.material?.surround?.type
		?? '';

	const depth = island.size?.depth;
	if (!depth || typeof depth !== 'object') return;
	const { left: depthLeft, right: depthRight } = depth;
	if (depthLeft == null || depthRight == null) return;

	const { frontPanelId } = panels;
	const frontPanel = findPanelById(panels.children, frontPanelId);
	if (!frontPanel) return;

	const leftSidePanelId = frontPanel.connections?.left?.targetPanelId;
	const rightSidePanelId = frontPanel.connections?.right?.targetPanelId;
	if (!leftSidePanelId || !rightSidePanelId) return;

	const leftSidePanel = findPanelById(panels.children, leftSidePanelId);
	const rightSidePanel = findPanelById(panels.children, rightSidePanelId);
	if (!leftSidePanel || !rightSidePanel) return;

	const leftBackPanelId = leftSidePanel.connections?.left?.targetPanelId;
	const rightBackPanelId = rightSidePanel.connections?.right?.targetPanelId;
	if (!leftBackPanelId || !rightBackPanelId) return;

	const leftBackPanel = findPanelById(panels.children, leftBackPanelId);
	const rightBackPanel = findPanelById(panels.children, rightBackPanelId);
	if (!leftBackPanel || !rightBackPanel) return;

	// Check for per-panel thickness overrides
	const { panelThickness } = island;
	const resolveThickness = (role: string): number => {
		const baseRole = (role === 'backLeft' || role === 'backRight') ? 'back' : role;
		const defaultThickness = getThicknessForRole(materialType, baseRole);
		if (!panelThickness) return defaultThickness;
		if (panelThickness.uniform) return panelThickness.thickness ?? defaultThickness;
		return panelThickness.panels?.[role] ?? panelThickness.panels?.[baseRole] ?? defaultThickness;
	};

	const frontThickness = resolveThickness('front');
	const backLeftThickness = resolveThickness('backLeft');
	const backRightThickness = resolveThickness('backRight');

	const bc = TARGET_FRONT_PANEL_WIDTH;
	const ea = depthLeft + (bc / Math.SQRT2);
	const ed = ea - backLeftThickness;
	const leftSideWidth = depthLeft - frontThickness - backLeftThickness;
	const rightSideWidth = depthRight - frontThickness - backRightThickness;
	const height = island.size?.height ?? frontPanel.size?.height;

	const setSize = (panel: any, width: number, thicknessRole: string): void => {
		const size = panel.size ?? panel.metadata?.size ?? {};
		size.width = width;
		size.height = height;
		size.depth = resolveThickness(thicknessRole);
		panel.size = size;
		if (panel.metadata) panel.metadata.size = size;
	};

	setSize(frontPanel, bc, 'front');
	setSize(leftSidePanel, leftSideWidth, 'left');
	setSize(rightSidePanel, rightSideWidth, 'right');
	setSize(leftBackPanel, nearestSixteenth(ea, true), 'backLeft');
	setSize(rightBackPanel, nearestSixteenth(ed, true), 'backRight');
};

export const u15 = (blueprint: any): any => {
	console.log('schema-update:process-update:15:started');

	try {
		const updatedBlueprint = deepCopy(blueprint);
		updatedBlueprint.schemaVersion = 15;

		if (updatedBlueprint.kitchens?.children) {
			updatedBlueprint.kitchens.children.forEach((kitchen: any) => {
				const islands = kitchen.design?.islands?.children;
				if (!islands?.length) return;

				islands.forEach((island: any) => {
					if (island.panels?.children?.length === 5) {
						fixCornerPanelSizes(island, kitchen);
					}
				});
			});
		}

		console.log('schema-update:process-update:15:completed');
		return updatedBlueprint;
	} catch (err) {
		console.error('schema-update:process-update:15:error', err);
		return null;
	}
};
